// src/components/reporting/usage/UsageSummaryPanel.js
// Reusable panel that renders the services + totals summary block.
// Identical data shape is returned at system level, tenant level, and (future) user level,
// so this single component works everywhere — only the title/context wrapper changes.

import React from 'react';
import PropTypes from 'prop-types';

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Convert cents (integer) → formatted dollar string, e.g. 1575 → "$15.75" */
export const centsToDollars = (cents) => {
  if (cents == null) return '$0.00';
  return `$${(cents / 100).toFixed(2)}`;
};

/** Format large token counts with commas */
const fmtNumber = (n) => {
  if (n == null) return '—';
  return Number(n).toLocaleString();
};

/** Format duration in seconds → "Xm Ys" */
const fmtDuration = (seconds) => {
  if (seconds == null) return '—';
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  if (m === 0) return `${s}s`;
  return `${m}m ${s}s`;
};

// ─── Service row definitions ─────────────────────────────────────────────────
// Each entry describes how to render one usageType row.
const SERVICE_DEFS = [
  {
    key: 'conversation',
    label: 'AI Conversations',
    icon: '💬',
    metrics: (svc) => [
      { label: 'Calls',         value: fmtNumber(svc.callCount) },
      { label: 'Input Tokens',  value: fmtNumber(svc.inputTokens) },
      { label: 'Output Tokens', value: fmtNumber(svc.outputTokens) },
    ],
  },
  {
    key: 'analytic_report',
    label: 'Analytic Reports',
    icon: '📊',
    metrics: (svc) => [
      { label: 'Calls',         value: fmtNumber(svc.callCount) },
      { label: 'Input Tokens',  value: fmtNumber(svc.inputTokens) },
      { label: 'Output Tokens', value: fmtNumber(svc.outputTokens) },
    ],
  },
  {
    key: 'ad_hoc_report',
    label: 'Ad Hoc Reports',
    icon: '🔍',
    metrics: (svc) => [
      { label: 'Calls',         value: fmtNumber(svc.callCount) },
      { label: 'Input Tokens',  value: fmtNumber(svc.inputTokens) },
      { label: 'Output Tokens', value: fmtNumber(svc.outputTokens) },
    ],
  },
  {
    key: 'assessment_report',
    label: 'Assessments',
    icon: '📋',
    metrics: (svc) => [
      { label: 'Reports',         value: fmtNumber(svc.callCount) },
      { label: 'Input Tokens',  value: fmtNumber(svc.inputTokens) },
      { label: 'Output Tokens', value: fmtNumber(svc.outputTokens) },
    ],
  },
  {
    key: 'stt',
    label: 'Speech-to-Text',
    icon: '🎙️',
    metrics: (svc) => [
      { label: 'Calls',    value: fmtNumber(svc.callCount) },
      { label: 'Duration', value: fmtDuration(svc.durationSeconds) },
      { label: '',         value: '' },
    ],
  },
  {
    key: 'tts',
    label: 'Text-to-Speech',
    icon: '🔊',
    metrics: (svc) => [
      { label: 'Calls',       value: fmtNumber(svc.callCount) },
      { label: 'Characters',  value: fmtNumber(svc.characterCount) },
      { label: '',            value: '' },
    ],
  },
];

// ─── Component ───────────────────────────────────────────────────────────────

const UsageSummaryPanel = ({ summary, periodLabel }) => {
  if (!summary || !summary.services) {
    return (
      <div className="usage-summary-panel usage-empty">
        No usage data available for this period.
      </div>
    );
  }

  const { services, totals } = summary;

  return (
    <div className="usage-summary-panel">
      {periodLabel && (
        <p className="usage-period-label">{periodLabel}</p>
      )}

      {/* Totals bar */}
      <div className="usage-totals-bar">
        <div className="usage-total-item">
          <span className="usage-total-value">{fmtNumber(totals?.totalCallCount)}</span>
          <span className="usage-total-label">Total Calls</span>
        </div>
        <div className="usage-totals-divider" />
        <div className="usage-total-item">
          <span className="usage-total-value usage-total-cost">{centsToDollars(totals?.totalCostCents)}</span>
          <span className="usage-total-label">Total Cost</span>
        </div>
      </div>

      {/* Per-service table */}
      <div className="usage-service-table">
        {/* Header */}
        <div className="usage-service-row usage-service-header">
          <div className="usage-service-name-col">Service</div>
          <div className="usage-service-metrics-col">
            <span>Calls</span>
            <span>Metric 1</span>
            <span>Metric 2</span>
          </div>
          <div className="usage-service-cost-col">Cost</div>
        </div>

        {/* Rows */}
        {SERVICE_DEFS.map(({ key, label, icon, metrics }) => {
          const svc = services[key];
          // Skip services with no data
          if (!svc || svc.callCount === 0) return null;

          const metricCells = metrics(svc);

          return (
            <div key={key} className="usage-service-row">
              <div className="usage-service-name-col">
                <span className="usage-service-icon">{icon}</span>
                <span className="usage-service-label">{label}</span>
              </div>
              <div className="usage-service-metrics-col">
                {metricCells.map((cell, i) => (
                  <span key={i} className="usage-metric-cell">
                    {cell.value ? (
                      <>
                        <span className="usage-metric-value">{cell.value}</span>
                        {cell.label && (
                          <span className="usage-metric-sublabel">{cell.label}</span>
                        )}
                      </>
                    ) : '—'}
                  </span>
                ))}
              </div>
              <div className="usage-service-cost-col">
                <span className="usage-service-cost">{centsToDollars(svc.totalCostCents)}</span>
              </div>
            </div>
          );
        })}

        {/* Zero-activity services note */}
        {SERVICE_DEFS.every(({ key }) => !services[key] || services[key].callCount === 0) && (
          <div className="usage-no-services">
            No service activity recorded for this period.
          </div>
        )}
      </div>
    </div>
  );
};

UsageSummaryPanel.propTypes = {
  summary: PropTypes.shape({
    services: PropTypes.object.isRequired,
    totals: PropTypes.shape({
      totalCostCents: PropTypes.number,
      totalCallCount: PropTypes.number,
    }),
  }),
  periodLabel: PropTypes.string,
};

export default UsageSummaryPanel;